import { useState, useEffect } from 'react';
import { AppSettings } from '../types';
import { getEnvironmentConfig, isLocalDevelopment, getSettingsUrl } from '../config/environment';

/**
 * Settings hook configuration
 */
export interface UseSettingsConfig {
  settingsFilePath?: string;
  fallbackSettings?: AppSettings;
  onError?: (error: Error) => void;
}

/**
 * Settings hook return type
 */
export interface UseSettingsReturn {
  settings: AppSettings | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Default settings for local development
 */
const DEFAULT_SETTINGS: AppSettings = {
  stage: 'local',
  cognitoConfig: {
    region: 'us-east-1',
    userPoolId: '',
    userPoolWebClientId: '',
    domain: '',
    redirectSignIn: 'http://localhost:9000',
    redirectSignOut: 'http://localhost:9000',
    responseType: 'code'
  }
};

/**
 * Custom hook to load and manage application settings
 * @param config - Configuration options for the hook
 * @returns Settings, loading state, error, and refetch function
 */
export const useSettings = (config?: UseSettingsConfig): UseSettingsReturn => {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const settingsPath = config?.settingsFilePath || getSettingsUrl();
  const fallbackSettings = config?.fallbackSettings || DEFAULT_SETTINGS;

  /**
   * Fetch settings from the specified path
   */
  const fetchSettings = async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(settingsPath);
      
      if (!response.ok) {
        throw new Error(`Failed to load settings: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      // Validate required fields
      if (!data.stage || !data.cognitoConfig) {
        throw new Error('Invalid settings format: missing required fields');
      }

      setSettings(data as AppSettings);
      // Store settings globally for API calls
      (window as any).__ADDAPT_SETTINGS__ = data;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error loading settings');
      
      console.error('Error loading settings:', error);
      setError(error);
      
      // Use fallback settings if provided
      if (fallbackSettings) {
        console.warn('Using fallback settings');
        setSettings(fallbackSettings);
      }
      
      // Call error handler if provided
      if (config?.onError) {
        config.onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Load settings on mount
  useEffect(() => {
    fetchSettings();
  }, [settingsPath]);

  return {
    settings,
    loading,
    error,
    refetch: fetchSettings
  };
};

/**
 * Get the public asset URL for a given path
 * @param path - The path to the asset
 * @returns The full URL to the asset
 */
export const publicAssetUrl = (path: string): string => {
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;
  
  // In production, assets might be served from a CDN
  // Use optional chaining and provide a fallback to avoid runtime errors
  const baseUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_ASSET_URL) 
    ? process.env.REACT_APP_ASSET_URL 
    : window.location.origin;
  
  return `${baseUrl}/${cleanPath}`;
};

/**
 * Hook to get settings with automatic retry on failure
 */
export const useSettingsWithRetry = (
  maxRetries: number = 3,
  retryDelay: number = 1000
): UseSettingsReturn => {
  const [retryCount, setRetryCount] = useState(0);
  
  const { settings, loading, error, refetch } = useSettings({
    onError: (err) => {
      if (retryCount < maxRetries) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          refetch();
        }, retryDelay * Math.pow(2, retryCount)); // Exponential backoff
      }
    }
  });

  return { settings, loading, error, refetch };
};

/**
 * Hook to get settings for a specific stage
 */
export const useStageSettings = (stage?: string): UseSettingsReturn => {
  const { settings, loading, error, refetch } = useSettings();
  
  // Override stage if provided
  const stageSettings = settings && stage ? {
    ...settings,
    stage: stage as AppSettings['stage']
  } : settings;

  return {
    settings: stageSettings,
    loading,
    error,
    refetch
  };
};
